﻿<script setup lang="ts">
    import { anonymizePeriod, startContactCount, countInactiveContacts, ContactCountRequest, ContactCountResult } from '@cwd/Account/DataManagement.access';
    import { reactive, onMounted, onUnmounted } from 'vue';
    import Loading from '@shared/components/Loading.vue';
    import { signalRService, initializeSignalR, OperationResult } from '@shared/services/signalR/signalr-service';

    const state = reactive({
        counted: false,
        numberOfContacts: 0,
        inactiveMonths: null,
        confirmAnonymization: false,
        anonymizationStarted: false,
        isLoading: false,
        step: 1,
        useBackgroundOperation: true, // Feature flag for using background operations
        currentRequestId: null as string | null,
        countingProgress: 0,
        countingMessage: '',
    });

    onMounted(async () => {
        // Initialize SignalR for background operations
        try {
            await initializeSignalR();
            console.log('SignalR initialized successfully');
            
            // Subscribe to events using the service pattern
            signalRService.addEventListener('completed', handleOperationCompleted);
            signalRService.addEventListener('progress', handleOperationProgress);
        } catch (error) {
            console.error('Failed to initialize SignalR:', error);
        }
    });

    onUnmounted(() => {
        // No need to manually unsubscribe as the service handles cleanup
        console.log('Component unmounted');
    });

    function handleOperationCompleted(result: any) {
        console.log('Received operation result:', result);
        
        if (result.RequestId === state.currentRequestId) {
            state.isLoading = false;
            state.currentRequestId = null;
            
            if (result.Success) {
                state.numberOfContacts = result.ContactCount || 0;
                state.counted = true;
                state.countingMessage = `Count completed in ${result.ExecutionTime || ''}`;
                console.log('Background count operation completed successfully:', state.numberOfContacts);
            } else {
                console.error('Contact count operation failed:', result.ErrorMessage);
                state.countingMessage = 'Background count operation failed';
            }
        }
    }

    function handleOperationProgress(progress: any) {
        console.log('Received operation progress:', progress);
        
        if (progress.RequestId === state.currentRequestId) {
            state.countingProgress = progress.PercentComplete || 0;
            state.countingMessage = progress.StatusMessage || 'Counting contacts...';
        }
    }

    async function count() {
        if (state.inactiveMonths === null) return;

        state.isLoading = true;
        state.counted = false;
        state.countingProgress = 0;
        state.countingMessage = 'Starting count...';

        try {
            if (state.useBackgroundOperation) {
                const request: ContactCountRequest = {
                    inactiveMonths: state.inactiveMonths
                };
                
                console.log('Starting background contact count with request:', request);
                const response = await startContactCount(request);
                console.log('Background operation started with response:', response);
                
                state.currentRequestId = response.RequestId;
                state.countingMessage = 'Background counting started...';
            } else {
                console.log('Starting synchronous contact count for', state.inactiveMonths, 'months');
                const contactCount = await countInactiveContacts(state.inactiveMonths);
                console.log('Synchronous count completed:', contactCount);
                
                state.numberOfContacts = contactCount;
                state.counted = true;
                state.countingMessage = 'Count completed';
                state.isLoading = false;
            }
        } catch (error) {
            console.error('Failed to complete count operation:', error);
            state.countingMessage = 'Error completing count operation';
            state.isLoading = false;
        }
    }

    async function anonymize() {
        if (state.inactiveMonths === null) {
            throw 'Error, inactiveMonths is null';
        }
        if (!state.confirmAnonymization) {
            throw 'Error, confirmation missing';
        }
        await anonymizePeriod(state.inactiveMonths);
        state.anonymizationStarted = true;
    }

    defineExpose({ state });
</script>
<script lang="ts">
    export default {
        name: 'AnonymizationManager',
    };
</script>

<template>
    <div class="max-700">
        <div v-if="state.step >= 1">
            <p>{{ $Resources.AnonymizationManagerInfoText }}</p>

            <h4 class="m-lg-t">{{ $Resources.AnonymizationManagerInactiveContactInfoHeading }}</h4>
            <p v-html="$Resources.AnonymizationManagerInactiveContactInfoText"></p>

            <h4 class="m-lg-t">{{ $Resources.AnonymizationManagerDataToDeleteHeading }}</h4>
            <ul v-html="$Resources.AnonymizationManagerDataToDeleteList"></ul>

            <h4 class="m-lg-t">{{ $Resources.AnonymizationManagerDataToAnonymizeHeading }}</h4>
            <ul v-html="$Resources.AnonymizationManagerDataToAnonymizeList"></ul>

            <div class="form-group m-xl-t" v-if="state.step == 1">
                <button type="button" class="btn btn-default" @click="state.step++">
                    {{ $Resources.AnonymizationManagerConfirmToContinueBtnText }}
                    <i class="fa fa-chevron-right" aria-hidden="true"></i>
                </button>
            </div>
        </div>
        <div v-if="state.step == 2">
            <h4 class="m-lg-t">{{ $Resources.AnonymizationManagerSelectTimePeriodHeading }}</h4>

            <div class="btn-group m-lg-b">
                <label class="btn btn-default" :class="{ 'btn-primary active': state.inactiveMonths == 12 }">
                    <input class="sr-only" value="12" type="radio" name="inactiveMonths1" v-model="state.inactiveMonths" v-on:change="count" />
                    12
                    {{ $Resources.AnonymizationManagerMonthsLabel }}
                </label>
                <label class="btn btn-default" :class="{ 'btn-primary active': state.inactiveMonths == 18 }">
                    <input class="sr-only" value="18" type="radio" name="inactiveMonths1" v-model="state.inactiveMonths" v-on:change="count" />
                    18
                    {{ $Resources.AnonymizationManagerMonthsLabel }}
                </label>
                <label class="btn btn-default" :class="{ 'btn-primary active': state.inactiveMonths == 24 }">
                    <input class="sr-only" value="24" type="radio" name="inactiveMonths1" v-model="state.inactiveMonths" v-on:change="count" />
                    24
                    {{ $Resources.AnonymizationManagerMonthsLabel }}
                </label>
                <label class="btn btn-default" :class="{ 'btn-primary active': state.inactiveMonths == 36 }">
                    <input class="sr-only" value="36" type="radio" name="inactiveMonths1" v-model="state.inactiveMonths" v-on:change="count" />
                    36
                    {{ $Resources.AnonymizationManagerMonthsLabel }}
                </label>
            </div>

            <div class="panel panel-default">
                <div class="panel-body">
                    <p class="c-light fw-600 m-xs-b">{{ $Resources.AnonymizationManagerNumberOfContactsLabel }}</p>
                    <p class="h1 strong m-0-t m-0-b">
                        <span>{{ state.numberOfContacts }}</span>
                    </p>
                    
                    <!-- Progress indicator for background operations -->
                    <div v-if="state.isLoading && state.useBackgroundOperation && state.currentRequestId" class="m-sm-t">
                        <div class="progress">
                            <div class="progress-bar" role="progressbar" :style="{ width: state.countingProgress + '%' }">
                                {{ state.countingProgress }}%
                            </div>
                        </div>
                        <small class="text-muted">{{ state.countingMessage }}</small>
                    </div>
                </div>
            </div>

            <div v-if="state.counted == true && state.numberOfContacts === 0" class="alert alert-success alert-fixed-i m-sm-t">
                <p>
                    <i class="fa fa-check" aria-hidden="true"></i>
                    <strong>{{ $Resources.AnonymizationManagerNoContactsToAnonymizeAlertHeading }}</strong>
                    {{ $Resources.AnonymizationManagerNoContactsToAnonymizeAlertText }}
                </p>
            </div>

            <div v-if="state.numberOfContacts > 0">
                <div class="alert alert-danger alert-fixed-i m-sm-b">
                    <p>
                        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                        <strong>{{ $Resources.AnonymizationManagerFinalConfirmAlertHeading }}</strong>
                        {{ $Resources.AnonymizationManagerFinalConfirmAlertTextPart1 }}
                        <strong>{{ state.numberOfContacts }}</strong>
                        {{ $Resources.AnonymizationManagerFinalConfirmAlertTextPart2 }}
                    </p>

                    <div class="form-group">
                        <div class="checkbox slide-toggle">
                            <label>
                                <input type="checkbox" v-model="state.confirmAnonymization" class="form-check-inline" />
                                <span class="toggle"></span>
                                {{ $Resources.AnonymizationManagerFinalConfirmLabel }}
                            </label>
                        </div>
                    </div>
                </div>

                <div class="form-group">
                    <button class="btn btn-danger btn-block" :disabled="!state.confirmAnonymization || state.anonymizationStarted" @click="anonymize">
                        {{ $Resources.AnonymizationManagerStartAnonymizationBtnText }}
                    </button>
                </div>
            </div>

            <Loading :show="state.isLoading"></Loading>

            <div v-if="state.anonymizationStarted === true">
                <div class="alert alert-success alert-fixed-i m-sm-t">
                    <p>
                        <i class="fa fa-check" aria-hidden="true"></i>
                        <strong>{{ $Resources.AnonymizationManagerAnonymizationStartedAlertHeading }}</strong>
                        {{ $Resources.AnonymizationManagerAnonymizationStartedAlertText }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
